import { Config } from "../../core/config/config";
import { Observable } from "../../utils/observable";
import type { AuthenticationManager } from "../authentication/authentication-manager";
import { SavingsScope, Scope, ServiceDomainScope, SharedServiceDomainScope } from "../authentication/scope";
import type { Session } from "../authentication/session";
import { sessionHasScope } from "../authentication/session";
import { Features } from "./features";

export class FeaturesManager {
  public features: Observable<Features> = new Observable<Features>(new Features());

  public constructor(private authenticationManager: AuthenticationManager) {
    this.updateFeatures(authenticationManager.session.get());
    this.authenticationManager.session.onChange.add((session) => this.updateFeatures(session));
  }

  private updateFeatures(session: Session | null): void {
    const features = {} as Features;
    if (session) {
      features.accounts = sessionHasScope(session, Scope.AccountsView);
      features.payment = sessionHasScope(session, Scope.Payment);
      features.payout = sessionHasScope(session, Scope.Payout);
      features.p2pTransfer = sessionHasScope(session, Scope.P2PTransfer);
      features.p2pSimpleTransfer = sessionHasScope(session, Scope.P2PSimpleTransfer);
      features.p2pCashTransfer = sessionHasScope(session, Scope.P2PCashTransfer);
      features.p2pDedicatedTransfer = sessionHasScope(session, Scope.P2PDedicatedTransfer);
      features.billPayment = sessionHasScope(session, Scope.BillPayment);
      features.transactionCode = sessionHasScope(session, Scope.TransactionCode);
      features.consultLoans = sessionHasScope(session, Scope.ConsultLoans);
      features.subscribeLoan = sessionHasScope(session, Scope.SubscribeLoan);
      features.cards = sessionHasScope(session, Scope.CardsManagement);
      features.virtualCardCreation = sessionHasScope(session, Scope.CardsVirtualCreation);
      features.clientInformationView = sessionHasScope(session, Scope.KYCView);
      features.clientInformationUpdate = sessionHasScope(session, Scope.KYCUpdate);
      features.pincodeUpdate = sessionHasScope(session, Scope.PincodeUpdate);
      features.notifications = sessionHasScope(session, Scope.Notifications);
      features.withdraw = sessionHasScope(session, Scope.Withdraw);
      features.walletRechargeByCard = sessionHasScope(session, Scope.WalletRechargeByCard);
      features.bankToWallet = sessionHasScope(session, Scope.BankToWallet);
      features.acceptorSearch = sessionHasScope(session, Scope.AcceptorSearch);
      features.manageQrCode = sessionHasScope(session, Scope.ManageQrCode);
      features.manageCategorizationSpendings = sessionHasScope(session, Scope.ManageCategorizationSpendings);
      features.bicReferential = sessionHasScope(session, Scope.BicReferential);
      features.externalAccountsManagement = sessionHasScope(session, Scope.ExternalAccountsManagement);
      features.externalAccountsUsage = sessionHasScope(session, Scope.ExternalAccountsUsage);
      features.beneficiariesManagement = sessionHasScope(session, Scope.BeneficiariesManagement);
      features.beneficiariesUsage = sessionHasScope(session, Scope.BeneficiariesUsage);
      features.sdaCustomerOnboardingCreate = sessionHasScope(session, Scope.SdaCustomerOnboardingCreate);
      features.sdaCustomerOnboardingView = sessionHasScope(session, Scope.SdaCustomerOnboardingView);
      features.sdaCustomerOnboardingUpdate = sessionHasScope(session, Scope.SdaCustomerOnboardingUpdate);
      features.sdaCustomerOnboardingSubmit = sessionHasScope(session, Scope.SdaCustomerOnboardingSubmit);
      features.strongCustomerAuthentication = sessionHasScope(session, Scope.StrongCustomerAuthentication);
      features.consultAuthorizationHolds = sessionHasScope(session, Scope.ConsultAuthorizationHolds);
      features.cardSensitiveDataView = sessionHasScope(session, Scope.CardSensitiveDataView);
      // features.sdaCardView = sessionHasScope(session, SharedServiceDomainScope.SdaCardView);
      features.sdaCustomerOnboardingActionsInitiate = sessionHasScope(
        session,
        Scope.SdaCustomerOnboardingActionsInitiate,
      );
      features.identityDocumentView = sessionHasScope(session, Scope.IdentityDocumentView);
      features.identityView = sessionHasScope(session, Scope.IdentityView);
      features.trustedDevices = sessionHasScope(session, Scope.TrustedDevices);
      features.mandateView = sessionHasScope(session, Scope.MandateView);
      features.mandateManagement = sessionHasScope(session, Scope.MandateManagement);

      // SERVICE DOMAIN
      features.additionalDataRead = sessionHasScope(session, ServiceDomainScope.AdditionalDataRead);
      features.customerInstructionView = sessionHasScope(session, SharedServiceDomainScope.CustomerInstructionView);
      features.customerInstructionInitiation = sessionHasScope(
        session,
        SharedServiceDomainScope.CustomerInstructionInitiation,
      );
      features.customerInstructionSubmit = sessionHasScope(session, SharedServiceDomainScope.CustomerInstructionSubmit);
      features.customerInstructionUpdate = sessionHasScope(session, SharedServiceDomainScope.CustomerInstructionUpdate);
      features.customerInstructionDeletion = sessionHasScope(
        session,
        SharedServiceDomainScope.CustomerInstructionDeletion,
      );
      features.paymentNetwork = sessionHasScope(session, SharedServiceDomainScope.PaymentNetwork);
      features.paymentContract = sessionHasScope(session, SharedServiceDomainScope.PaymentContract);
      features.sdaAuthenticationsSessionsView = sessionHasScope(
        session,
        SharedServiceDomainScope.SdaAuthenticationsSessionsView,
      );
      features.sdaAuthenticationsSessionsAuth = sessionHasScope(
        session,
        SharedServiceDomainScope.SdaAuthenticationsSessionsAuth,
      );
      features.paymentInstructionView = sessionHasScope(session, SharedServiceDomainScope.PaymentInstructionView);
      features.sdaAccountView = sessionHasScope(session, SharedServiceDomainScope.SdaAccountView);
      features.sdaExchangeRateView = sessionHasScope(session, SharedServiceDomainScope.SdaExchangeRateView);
      features.recipientCreate = sessionHasScope(session, SharedServiceDomainScope.RecipientCreate);
      features.sdaTransactionsView = sessionHasScope(session, SharedServiceDomainScope.SdaTransactionsView);
      features.sdaAccountsNameUpdate = sessionHasScope(session, SharedServiceDomainScope.SdaAccountsNameUpdate);
      features.paymentStandingOrderCreate = sessionHasScope(
        session,
        SharedServiceDomainScope.PaymentStandingOrderCreate,
      );
      features.paymentStandingOrderUpdate = sessionHasScope(
        session,
        SharedServiceDomainScope.PaymentStandingOrderUpdate,
      );
      features.paymentStandingOrderDelete = sessionHasScope(
        session,
        SharedServiceDomainScope.PaymentStandingOrderDelete,
      );
      features.paymentStandingOrderView = sessionHasScope(session, SharedServiceDomainScope.PaymentStandingOrderView);

      // SAVINGS API
      features.savContractRead = sessionHasScope(session, SavingsScope.SavContractRead);
      features.sdaRequiredActionsWrite = sessionHasScope(session, SharedServiceDomainScope.SdaRequiredActionsWrite);
      features.sdaAccountsNameUpdate = sessionHasScope(session, SharedServiceDomainScope.SdaAccountsNameUpdate);
      features.savDeposit = sessionHasScope(session, SavingsScope.SavDeposit);
      features.savProductRead = sessionHasScope(session, SavingsScope.SavProductRead);
      features.savContractWrite = sessionHasScope(session, SavingsScope.SavContractWrite);
      features.savDocumentsRead = sessionHasScope(session, SavingsScope.SavDocumentsRead);
    }

    features.externalLoanManagement = Config.EXTERNAL_LOAN_MANAGEMENT;
    this.features.set({
      ...this.features.get(),
      ...features,
    });
  }
}
